import React, { useState } from 'react';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { eventsAPI } from '../../../services/api';
import { STORAGE_PATHS } from '../../../config/constants';
import LocationSelector from '../../common/Forms/LocationSelector';
import { partners } from '../Partners/PartnersSection';
import './EditActivityForm.css'; // Reuse the same CSS

function EditEventForm({ event, onClose, onEventUpdated }) {
    const [formData, setFormData] = useState({
        title: event.title || '',
        description: event.description || '',
        date: event.date ? event.date.split('T')[0] : '',
        country: event.country || '',
        city: event.city || '',
        requirements: event.requirements || '',
        registrationLink: event.registrationLink || '',
        partners: event.partners || []
    });
    
    const [imageFile, setImageFile] = useState(null);
    const [previewImage, setPreviewImage] = useState(event.imageUrl || null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState('');

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5242880) { // 5MB limit
                setError('Image size should be less than 5MB');
                return;
            }
            setImageFile(file);
            setPreviewImage(URL.createObjectURL(file));
            setError('');
        }
    };

    const handleRemoveImage = () => {
        setImageFile(null);
        setPreviewImage(null);
    };

    const handlePartnerChange = (e) => {
        const selectedPartners = Array.from(e.target.selectedOptions, option => option.value);
        setFormData(prev => ({
            ...prev,
            partners: selectedPartners
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setMessage('');

        try {
            // Prepare event data
            const eventData = {
                ...formData,
                startDate: formData.date,
                endDate: formData.date,
                type: event.type || 'upcoming',
                status: event.status || 'scheduled',
                content: formData.description
            };
            
            // Keep the existing image if no new one is uploaded
            if (!imageFile && event.imageUrl) {
                eventData.imageUrl = event.imageUrl;
            }

            // Upload new image if selected
            if (imageFile) {
                try {
                    setMessage('Uploading image...');
                    const storage = getStorage();
                    const imagePath = `${STORAGE_PATHS.EVENT_IMAGES || 'event-images'}/${Date.now()}_${imageFile.name}`;
                    const imageRef = storageRef(storage, imagePath);
                    
                    // Upload the file
                    const uploadResult = await uploadBytes(imageRef, imageFile);
                    
                    // Get download URL and add to event data
                    const downloadURL = await getDownloadURL(uploadResult.ref);
                    
                    eventData.imageUrl = downloadURL;
                    setMessage('Image uploaded successfully');
                } catch (uploadError) {
                    setError('Failed to upload image: ' + uploadError.message);
                    setLoading(false);
                    return;
                }
            }
            
            // Call API to update event
            await eventsAPI.updateEvent(event.id, eventData);
            
            setMessage('Event updated successfully');
            setTimeout(() => {
                onEventUpdated();
            }, 1000);
            
        } catch (error) {
            setError(error.message || 'Failed to update event');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="edit-form-overlay">
            <div className="edit-form">
                <h2>Edit Event</h2>
                
                {error && <div className="error-message">{error}</div>}
                {message && <div className="success-message">{message}</div>}
                
                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label>Title *</label>
                        <input
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={handleInputChange}
                            required
                        />
                    </div>
                    
                    <div className="form-group">
                        <label>Description *</label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            required
                            rows="4"
                        />
                    </div>
                    
                    <div className="form-group">
                        <label>Date *</label>
                        <input
                            type="date"
                            name="date"
                            value={formData.date}
                            onChange={handleInputChange}
                            required
                        />
                    </div>
                    
                    <div className="form-group">
                        <label>Location *</label>
                        <LocationSelector
                            countryCode={formData.country}
                            city={formData.city}
                            onCountryChange={(code) => {
                                setFormData(prev => ({
                                    ...prev,
                                    country: code,
                                    city: ''
                                }));
                            }}
                            onCityChange={(cityName) => {
                                setFormData(prev => ({
                                    ...prev,
                                    city: cityName
                                }));
                            }}
                            required={true}
                        />
                    </div>
                    
                    <div className="form-group">
                        <label>Requirements</label>
                        <textarea
                            name="requirements"
                            value={formData.requirements}
                            onChange={handleInputChange}
                            rows="3"
                            placeholder="Each requirement on a new line"
                        />
                    </div>
                    
                    <div className="form-group">
                        <label>Registration Link</label>
                        <input
                            type="url"
                            name="registrationLink"
                            value={formData.registrationLink}
                            onChange={handleInputChange}
                            placeholder="https://..."
                        />
                    </div>
                    
                    <div className="form-group">
                        <label>Partners</label>
                        <div className="partners-checkbox-list">
                            {partners.map((partner, index) => (
                                <label key={index} className="partner-checkbox-item">
                                    <input
                                        type="checkbox"
                                        value={partner.name}
                                        checked={formData.partners?.includes(partner.name)}
                                        onChange={(e) => {
                                            const partnerName = e.target.value;
                                            setFormData(prev => ({
                                                ...prev,
                                                partners: e.target.checked
                                                    ? [...(prev.partners || []), partnerName]
                                                    : (prev.partners || []).filter(p => p !== partnerName)
                                            }));
                                        }}
                                    />
                                    <span>{partner.name}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                    
                    <div className="form-group">
                        <label className="form-label">Event Image</label>
                        <input
                            type="file"
                            id="editEventImageInput"
                            accept="image/*"
                            onChange={handleFileChange}
                            className="form-file-input"
                            style={{ display: 'none' }}
                        />
                        <label htmlFor="editEventImageInput" className="form-file-label">
                            <i className="fas fa-upload"></i>
                            {imageFile ? imageFile.name : (previewImage ? 'Change Image' : 'Choose Image')}
                        </label>
                        
                        {previewImage && (
                            <div className="form-file-preview">
                                <div className="preview-item">
                                    <img src={previewImage} alt="Event preview" />
                                    <button
                                        type="button"
                                        className="remove-image"
                                        onClick={handleRemoveImage}
                                    >
                                        &times;
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                    
                    <div className="form-actions">
                        <button type="submit" disabled={loading}>
                            {loading ? 'Updating...' : 'Update Event'}
                        </button>
                        <button type="button" onClick={onClose}>
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default EditEventForm; 